import {
  LOG_LONG_CONTENT_PROPS,
  formatJsonLikeLogText,
  splitLogParamPrefix
} from './logTextFormat.mjs';

const LONG_CONTENT_PROPS = LOG_LONG_CONTENT_PROPS;
const MAX_VISIBLE_LINE_COUNT = 6;
const EXCEL_LINE_HEIGHT_PX = 18;
const EXCEL_CELL_VERTICAL_PADDING_PX = 12;
const EXCEL_POINT_RATIO = 0.75;
const EXCEL_HEADER_ROW_HEIGHT = 24 * EXCEL_POINT_RATIO;
const ZIP_DOS_DATE_1980_01_01 = 33;

export const LOG_EXCEL_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
export const LOG_EXCEL_EXTENSION = 'xlsx';

const COLUMN_WIDTHS = {
  SnType: 90,
  Version: 90,
  Vin: 180,
  DiagParam: 520,
  ResData: 520,
  FuncType: 90,
  CreatedAt: 190,
  DiagId: 140,
  Sn: 150,
  runtime: 90,
  Ip: 140,
  Language: 80,
  ProductType: 100,
  LocalMac: 140,
  WifiMac: 140,
  WifiName: 140,
};

function escapeXml(value) {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, ' ')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function escapeSheetName(value) {
  return escapeXml(String(value).replace(/[\[\]:*?/\\]/g, '-').slice(0, 31) || 'online-log');
}

function splitParamPrefix(value) {
  const { prefix, content } = splitLogParamPrefix(value, 'DiagParam');
  return prefix ? { prefix: `${prefix}:`, content } : { prefix: '', content: String(value) };
}

function fallbackFormatJsonLike(value) {
  return String(value)
    .replace(/\{/g, '{\n  ')
    .replace(/\}/g, '\n}')
    .replace(/\[/g, '[\n  ')
    .replace(/\]/g, '\n]')
    .replace(/,/g, ',\n  ')
    .replace(/\n\s*\n/g, '\n')
    .trim();
}

function formatJsonLike(value) {
  const trimmed = formatJsonLikeLogText(value, { pretty: true });
  if (!trimmed) return '';

  try {
    return JSON.stringify(JSON.parse(trimmed), null, 2);
  } catch (error) {
    if (/^[\[{]/.test(trimmed)) {
      return fallbackFormatJsonLike(trimmed);
    }
    return trimmed;
  }
}

export function formatExcelCellValue(value, prop) {
  if (value === null || value === undefined) return '';
  const raw = String(value);
  if (!LONG_CONTENT_PROPS.has(prop)) return raw;

  const { prefix, content } = splitParamPrefix(raw);
  const formatted = formatJsonLike(content);
  if (!prefix) return formatted;
  return formatted.includes('\n') ? `${prefix}\n${formatted}` : `${prefix} ${formatted}`;
}

function excelColumnWidth(prop) {
  return Math.round(((COLUMN_WIDTHS[prop] || 120) / 7) * 10) / 10;
}

function visualLength(value) {
  return String(value).replace(/[^\x00-\xff]/g, 'xx').length;
}

function estimatedWrappedLineCount(value, prop) {
  if (!LONG_CONTENT_PROPS.has(prop)) return 1;
  const text = String(value || '');
  if (!text) return 1;

  const columnWidth = COLUMN_WIDTHS[prop] || 120;
  const charsPerLine = Math.max(8, Math.floor(columnWidth / 8));
  return text
    .split(/\r\n|\r|\n/)
    .reduce((lineCount, line) => lineCount + Math.max(1, Math.ceil(visualLength(line) / charsPerLine)), 0);
}

function excelRowHeightForCells(cells) {
  const estimatedLines = cells.reduce((maxLines, cell) => {
    return Math.max(maxLines, estimatedWrappedLineCount(cell.value, cell.column.prop));
  }, 1);
  const visibleLines = Math.min(Math.max(estimatedLines, 1), MAX_VISIBLE_LINE_COUNT);
  return (visibleLines * EXCEL_LINE_HEIGHT_PX + EXCEL_CELL_VERTICAL_PADDING_PX) * EXCEL_POINT_RATIO;
}

function columnName(index) {
  let value = index + 1;
  let name = '';
  while (value > 0) {
    const remainder = (value - 1) % 26;
    name = String.fromCharCode(65 + remainder) + name;
    value = Math.floor((value - 1) / 26);
  }
  return name;
}

function styleIndex(prop, isAltRow = false, isHeader = false) {
  if (isHeader) return 1;
  if (LONG_CONTENT_PROPS.has(prop)) return isAltRow ? 4 : 3;
  return isAltRow ? 2 : 0;
}

function cellXml(rowIndex, columnIndex, value, prop, isAltRow = false, isHeader = false) {
  const reference = `${columnName(columnIndex)}${rowIndex}`;
  const style = styleIndex(prop, isAltRow, isHeader);
  return `<c r="${reference}" s="${style}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(value)}</t></is></c>`;
}

function buildWorksheetXml(columns, rows) {
  const safeColumns = Array.isArray(columns) ? columns : [];
  const safeRows = Array.isArray(rows) ? rows : [];
  const lastColumnName = columnName(Math.max(safeColumns.length - 1, 0));
  const lastRowIndex = Math.max(safeRows.length + 1, 1);
  const columnXml = safeColumns
    .map((column, index) => `<col min="${index + 1}" max="${index + 1}" width="${excelColumnWidth(column.prop)}" customWidth="1"/>`)
    .join('');
  const headerHtml = safeColumns
    .map((column, index) => cellXml(1, index, column.label, column.prop, false, true))
    .join('');
  const bodyHtml = safeRows
    .map((row, index) => {
      const isAltRow = index % 2 === 1;
      const rowIndex = index + 2;
      const formattedCells = safeColumns.map(column => ({
        column,
        value: formatExcelCellValue(row[column.prop], column.prop),
      }));
      const rowHeight = excelRowHeightForCells(formattedCells);
      const cells = formattedCells
        .map((cell, columnIndex) => cellXml(rowIndex, columnIndex, cell.value, cell.column.prop, isAltRow))
        .join('');
      return `<row r="${rowIndex}" ht="${rowHeight}" customHeight="1">${cells}</row>`;
    })
    .join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <dimension ref="A1:${lastColumnName}${lastRowIndex}"/>
  <sheetViews>
    <sheetView workbookViewId="0">
      <pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/>
      <selection pane="bottomLeft" activeCell="A2" sqref="A2"/>
    </sheetView>
  </sheetViews>
  <sheetFormatPr defaultRowHeight="18"/>
  <cols>${columnXml}</cols>
  <sheetData>
    <row r="1" ht="${EXCEL_HEADER_ROW_HEIGHT}" customHeight="1">${headerHtml}</row>
    ${bodyHtml}
  </sheetData>
</worksheet>`;
}

function buildWorkbookXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>
    <sheet name="${escapeSheetName('online-log')}" sheetId="1" r:id="rId1"/>
  </sheets>
</workbook>`;
}

function buildWorkbookRelsXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
  <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;
}

function buildRootRelsXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;
}

function buildContentTypesXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
</Types>`;
}

function buildStylesXml() {
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="2">
    <font><sz val="10"/><color rgb="FF1F2933"/><name val="Microsoft YaHei"/></font>
    <font><b/><sz val="10"/><color rgb="FF1F2933"/><name val="Microsoft YaHei"/></font>
  </fonts>
  <fills count="4">
    <fill><patternFill patternType="none"/></fill>
    <fill><patternFill patternType="gray125"/></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFEAF2FF"/><bgColor indexed="64"/></patternFill></fill>
    <fill><patternFill patternType="solid"><fgColor rgb="FFF6F8FA"/><bgColor indexed="64"/></patternFill></fill>
  </fills>
  <borders count="1">
    <border>
      <left style="thin"><color rgb="FFD9E2EC"/></left>
      <right style="thin"><color rgb="FFD9E2EC"/></right>
      <top style="thin"><color rgb="FFD9E2EC"/></top>
      <bottom style="thin"><color rgb="FFD9E2EC"/></bottom>
      <diagonal/>
    </border>
  </borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="5">
    <xf numFmtId="49" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment vertical="top"/></xf>
    <xf numFmtId="49" fontId="1" fillId="2" borderId="0" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="top"/></xf>
    <xf numFmtId="49" fontId="0" fillId="3" borderId="0" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment vertical="top"/></xf>
    <xf numFmtId="49" fontId="0" fillId="0" borderId="0" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf>
    <xf numFmtId="49" fontId="0" fillId="3" borderId="0" xfId="0" applyNumberFormat="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment vertical="top" wrapText="1"/></xf>
  </cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;
}

const crc32Table = Array.from({ length: 256 }, (_, index) => {
  let value = index;
  for (let bit = 0; bit < 8; bit += 1) {
    value = (value & 1) ? (0xedb88320 ^ (value >>> 1)) : (value >>> 1);
  }
  return value >>> 0;
});

function crc32(bytes) {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc = crc32Table[(crc ^ byte) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function writeUInt16(view, offset, value) {
  view.setUint16(offset, value, true);
}

function writeUInt32(view, offset, value) {
  view.setUint32(offset, value >>> 0, true);
}

function concatBytes(chunks) {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const output = new Uint8Array(totalLength);
  let offset = 0;
  chunks.forEach((chunk) => {
    output.set(chunk, offset);
    offset += chunk.length;
  });
  return output;
}

function buildZip(files) {
  const encoder = new TextEncoder();
  const localParts = [];
  const centralParts = [];
  let localOffset = 0;

  files.forEach((file) => {
    const nameBytes = encoder.encode(file.name);
    const dataBytes = encoder.encode(file.content);
    const checksum = crc32(dataBytes);

    const localHeader = new Uint8Array(30 + nameBytes.length);
    const localView = new DataView(localHeader.buffer);
    writeUInt32(localView, 0, 0x04034b50);
    writeUInt16(localView, 4, 20);
    writeUInt16(localView, 6, 0);
    writeUInt16(localView, 8, 0);
    writeUInt16(localView, 10, 0);
    writeUInt16(localView, 12, ZIP_DOS_DATE_1980_01_01);
    writeUInt32(localView, 14, checksum);
    writeUInt32(localView, 18, dataBytes.length);
    writeUInt32(localView, 22, dataBytes.length);
    writeUInt16(localView, 26, nameBytes.length);
    writeUInt16(localView, 28, 0);
    localHeader.set(nameBytes, 30);
    localParts.push(localHeader, dataBytes);

    const centralHeader = new Uint8Array(46 + nameBytes.length);
    const centralView = new DataView(centralHeader.buffer);
    writeUInt32(centralView, 0, 0x02014b50);
    writeUInt16(centralView, 4, 20);
    writeUInt16(centralView, 6, 20);
    writeUInt16(centralView, 8, 0);
    writeUInt16(centralView, 10, 0);
    writeUInt16(centralView, 12, 0);
    writeUInt16(centralView, 14, ZIP_DOS_DATE_1980_01_01);
    writeUInt32(centralView, 16, checksum);
    writeUInt32(centralView, 20, dataBytes.length);
    writeUInt32(centralView, 24, dataBytes.length);
    writeUInt16(centralView, 28, nameBytes.length);
    writeUInt16(centralView, 30, 0);
    writeUInt16(centralView, 32, 0);
    writeUInt16(centralView, 34, 0);
    writeUInt16(centralView, 36, 0);
    writeUInt32(centralView, 38, 0);
    writeUInt32(centralView, 42, localOffset);
    centralHeader.set(nameBytes, 46);
    centralParts.push(centralHeader);

    localOffset += localHeader.length + dataBytes.length;
  });

  const centralDirectory = concatBytes(centralParts);
  const endRecord = new Uint8Array(22);
  const endView = new DataView(endRecord.buffer);
  writeUInt32(endView, 0, 0x06054b50);
  writeUInt16(endView, 4, 0);
  writeUInt16(endView, 6, 0);
  writeUInt16(endView, 8, files.length);
  writeUInt16(endView, 10, files.length);
  writeUInt32(endView, 12, centralDirectory.length);
  writeUInt32(endView, 16, localOffset);
  writeUInt16(endView, 20, 0);

  return concatBytes([...localParts, centralDirectory, endRecord]);
}

export function buildLogExcelFile(columns, rows) {
  return buildZip([
    { name: '[Content_Types].xml', content: buildContentTypesXml() },
    { name: '_rels/.rels', content: buildRootRelsXml() },
    { name: 'xl/workbook.xml', content: buildWorkbookXml() },
    { name: 'xl/_rels/workbook.xml.rels', content: buildWorkbookRelsXml() },
    { name: 'xl/styles.xml', content: buildStylesXml() },
    { name: 'xl/worksheets/sheet1.xml', content: buildWorksheetXml(columns, rows) },
  ]);
}
